import React, { useEffect, useRef, useState } from "react";
import { useStepNavigate } from "../hooks/useStepNavigate";
import { dataService } from "../services/dataService";
import { useOrder } from "../context/OrderContext";
import { useStaticOptions } from "../context/StaticOptionsContext";
import TablerLayout from "../components/layout/TablerLayout";
import StepperNavButton from "../components/stepper/StepperNavButton";
import PaginatedTable from "../components/PaginatedTable";

const STORAGE_KEY = "rendelesek_filters";
const PAGE_KEY = "rendelesek_current_page";
const LIMIT_KEY = "rendelesek_items_per_page";

const defaultFilters = {
    hmeliorder_depot_prefix: "",
    hmeliorder_specific_id: "",
    partner_name: "",
    partner_taj: "",
};

const Rendelesek = () => {
    const { replaceOrder } = useOrder();
    const { staticOptions } = useStaticOptions();
    const userPrefsApplied = useRef(false);
    const navigate = useStepNavigate();

    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState(() => {
        try {
            const saved = sessionStorage.getItem(STORAGE_KEY);
            return saved ? JSON.parse(saved) : defaultFilters;
        } catch {
            return defaultFilters;
        }
    });

    const [currentPage, setCurrentPage] = useState(() => {
        const saved = sessionStorage.getItem(PAGE_KEY);
        return saved ? Number(saved) : 1;
    });

    const [totalCount, setTotalCount] = useState(0);

    const [itemsPerPage, setItemsPerPage] = useState(() => {
        const saved = sessionStorage.getItem(LIMIT_KEY);
        return saved ? Number(saved) : 10;
    });

    const [locations, setLocations] = useState([]);

    const columns = [
        { title: "Méretfelvételi hely", dataKey: "hmeliorder_depot_prefix" },
        { title: "Méretlap szám", dataKey: "hmeliorder_specific_id" },
        { title: "Megrendelő", dataKey: "partner_name" },
        { title: "Státusz", dataKey: "hmeliorder_status" },
        { title: "Gyártási státusz", dataKey: "hmeliorder_factory_stat_name" },
        { title: "Megrendelés dátuma", dataKey: "hmeliorder_order_date" },
    ];

    useEffect(() => {
        const fetchLocations = async () => {
            try {
                const response = await dataService.getLocations();
                if (response.data?.status === "ok") {
                    setLocations(response.data.data || []);
                }
            } catch (error) {
                console.error("Hiba a méretfelvételi helyek betöltésekor:", error);
            }
        };

        fetchLocations();
    }, []);

    useEffect(() => {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
    }, [filters]);

    useEffect(() => {
        sessionStorage.setItem(PAGE_KEY, String(currentPage));
    }, [currentPage]);

    useEffect(() => {
        sessionStorage.setItem(LIMIT_KEY, String(itemsPerPage));
    }, [itemsPerPage]);

    const filterFields = [
        {
            title: "Méretfelvételi hely",
            dataKey: "hmeliorder_depot_prefix",
            placeholder: "Méretfelvételi hely szerint...",
            type: "dropdown",
            options: locations.map((loc) => ({
                value: loc.depot_prefix,
                label: loc.depot_prefix,
            })),
        },
        {
            title: "Méretlap szám",
            dataKey: "hmeliorder_specific_id",
            placeholder: "Méretlap szám szerint...",
        },
        {
            title: "Megrendelő neve",
            dataKey: "partner_name",
            placeholder: "Megrendelő neve szerint...",
        },
        {
            title: "TAJ azonosító",
            dataKey: "partner_taj",
            placeholder: "TAJ szerint...",
        },
    ];

    const sanitizeToDigits = (value = "") => value.replace(/\D/g, "");

    const mapPrefsToFilters = (prefs = {}) => ({
        hmeliorder_depot_prefix: prefs.hmeliorder_depot_prefix || "",
        hmeliorder_specific_id: prefs.hmeliorder_specific_id || "",
        partner_name: prefs.partner_name || "",
        partner_taj: sanitizeToDigits(prefs.partner_taj || ""),
    });

    const fetchOrders = async (
        page = currentPage,
        filterObj = filters,
        limit = itemsPerPage
    ) => {
        setLoading(true);
        setOrders([]);

        try {
            const offset = (page - 1) * limit;
            const filter = {};

            Object.entries(filterObj).forEach(([k, v]) => {
                if (v && String(v).trim() !== "") {
                    filter[k] = v;
                }
            });

            const response = await dataService.getOrders({
                limit,
                offset,
                filter,
            });

            if (response.data?.status === "ok") {
                setOrders(Array.isArray(response.data.data) ? response.data.data : []);
                setTotalCount(response.data.count || 0);
            } else {
                setOrders([]);
                setTotalCount(0);
            }
        } catch (err) {
            console.error("Hiba a rendelések lekérésekor:", err);
            setOrders([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const prefs = staticOptions?.user_prefs;
        if (!prefs || userPrefsApplied.current) {
            return;
        }

        const normalized = mapPrefsToFilters(prefs || {});
        userPrefsApplied.current = true;
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
        setFilters(normalized);
        setCurrentPage(1);
        fetchOrders(1, normalized, itemsPerPage);
    }, [staticOptions, itemsPerPage]);

    const handleRendelesSelect = (order) => {
        if (!order || !order.hmeliorder_szam) {
            alert("Nincs kiválasztva rendelés!");
            return;
        }

        setLoading(true);

        dataService
            .getOrder(order.hmeliorder_szam, order.partner_szam)
            .then((response) => {
                if (response.data?.status === "ok") {
                    const orderData = response.data.data;
                    replaceOrder(orderData);
                    navigate("/RendelesAdatlap");
                } else {
                    alert("Hiba történt a rendelés betöltésekor!");
                }
            })
            .catch((err) => {
                console.error("Error fetching order:", err);
                alert("Hiba történt a rendelés betöltésekor!");
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleBackClick = () => {
        navigate("/Home");
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        const nextValue = name === "partner_taj" ? sanitizeToDigits(value) : value;

        const newFilters = {
            ...filters,
            [name]: nextValue,
        };

        setFilters(newFilters);
        setCurrentPage(1);
        fetchOrders(1, newFilters, itemsPerPage);
    };

    const handleClearFilter = (fieldName) => {
        const newFilters = {
            ...filters,
            [fieldName]: "",
        };

        setFilters(newFilters);
        setCurrentPage(1);
        fetchOrders(1, newFilters, itemsPerPage);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
        fetchOrders(page, filters, itemsPerPage);
    };

    const handleLimitChange = (newLimit) => {
        setItemsPerPage(newLimit);
        setCurrentPage(1);
        fetchOrders(1, filters, newLimit);
    };

    return (
        <TablerLayout title="Rendelés keresés">
            <div className="row justify-content-center">
                <div className="col-md-12 mb-4">
                    <div className="card-body p-0">
                        <PaginatedTable
                            columns={columns}
                            data={orders}
                            filterFields={filterFields}
                            filters={filters}
                            onFilterChange={handleFilterChange}
                            onClearFilter={handleClearFilter}
                            onPageChange={handlePageChange}
                            onLimitChange={handleLimitChange}
                            onRowClick={handleRendelesSelect}
                            itemsPerPage={itemsPerPage}
                            currentPage={currentPage}
                            totalCount={totalCount}
                            rowKey={(row, idx) =>
                                row.hmeliorder_szam
                                    ? `${row.hmeliorder_szam}_${idx}`
                                    : idx
                            }
                            loading={loading}
                            renderCustomFilter={(field) => {
                                if (field.dataKey === "hmeliorder_depot_prefix") {
                                    return (
                                        <div className="mb-3" key={field.dataKey}>
                                            <label className="form-label">{field.title}</label>

                                            <div className="filter-input-wrapper">
                                                <select
                                                    name={field.dataKey}
                                                    value={filters[field.dataKey]}
                                                    onChange={handleFilterChange}
                                                    className="form-select filter-control"
                                                >
                                                    <option value="">Összes méretfelvételi hely</option>
                                                    {locations.map((loc) => (
                                                        <option
                                                            key={loc.depot_prefix}
                                                            value={loc.depot_prefix}
                                                        >
                                                            {loc.depot_prefix}
                                                        </option>
                                                    ))}
                                                </select>

                                                {filters[field.dataKey] && (
                                                    <button
                                                        type="button"
                                                        className="filter-clear-btn"
                                                        onClick={() => handleClearFilter(field.dataKey)}
                                                        aria-label={`${field.title} törlése`}
                                                        title="Törlés"
                                                    >
                                                        ×
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                }

                                return null;
                            }}
                        />
                    </div>
                </div>

                <div className="d-flex justify-content-between mt-4">
                    <StepperNavButton side="left" label="Vissza" onClick={handleBackClick} />
                </div>
            </div>
        </TablerLayout>
    );
};

export default Rendelesek;